/**
 * License + ownership banners. Renders into a host element (or auto-finds
 * `#licenseBanner` / `#ownershipBanner`). Replaces:
 *   - `license-banner.js::upsertLicenseBanner`
 *   - `license-banner.js::upsertOwnershipBanner`
 *   - `license-banner.js::renderLicenseBannerAndExpiry`
 *   - `license-banner.js::renderOwnershipBanner`
 *
 * Status comes from `license.service.js` (single source of truth for the
 * red/orange/green logic).
 */

import { LocalState } from '../../core/localState.js';
import { ref, readOnce } from '../../core/firebase.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { createLogger } from '../../core/logger.js';
import { getStatus } from './license.service.js';
import { getStoredOwnershipISO, getLastOwnership, persistOwnership } from './ownership.service.js';
import { isoToYearMonth } from '../../shared/date.js';

const log = createLogger('license-banner');

const BANNER_LICENSE_ID = 'licenseBanner';
const BANNER_OWNERSHIP_ID = 'ownershipBanner';

/**
 * Render both banners into the given host (or auto-find them). Idempotent —
 * existing nodes are updated in place.
 *
 * @param {string | HTMLElement} [host='accountContent']
 */
export async function render(host = 'accountContent') {
  const hostEl = resolveHost(host);
  if (!hostEl) return;

  const plate = normalizePlate(LocalState.plate ?? '');
  if (!plate) {
    setBanner(hostEl, BANNER_LICENSE_ID, 'unknown', 'אין רכב משויך לחשבון.');
    return;
  }
  await Promise.all([renderLicense(hostEl, plate), renderOwnership(hostEl, plate)]);
}

/** Convenience: render just the license expiry banner. */
export async function renderLicenseOnly(host = 'accountContent') {
  const hostEl = resolveHost(host);
  if (!hostEl) return;
  const plate = normalizePlate(LocalState.plate ?? '');
  if (!plate) return;
  await renderLicense(hostEl, plate);
}

/** Convenience: render just the ownership banner. */
export async function renderOwnershipOnly(host = 'accountContent') {
  const hostEl = resolveHost(host);
  if (!hostEl) return;
  const plate = normalizePlate(LocalState.plate ?? '');
  if (!plate) return;
  await renderOwnership(hostEl, plate);
}

// ====================================================================
// internals
// ====================================================================

/** @param {string | HTMLElement} host */
function resolveHost(host) {
  if (typeof host !== 'string') return host;
  return document.getElementById(host);
}

/**
 * @param {HTMLElement} hostEl
 * @param {string} plate
 */
async function renderLicense(hostEl, plate) {
  try {
    const info = await getStatus(plate);
    if (info.status === 'unknown') {
      setBanner(hostEl, BANNER_LICENSE_ID, 'orange', 'מאתר את תוקף רישיון הרכב…');
      return;
    }
    const formatted = info.prettyDate ?? '';
    if (info.status === 'red') {
      setBanner(hostEl, BANNER_LICENSE_ID, 'red',
        `⚠ תוקף רישיון הרכב פג (${formatted}). יש לחדש בדחיפות.`,
        { showRenewBtn: true });
    } else if (info.status === 'orange') {
      setBanner(hostEl, BANNER_LICENSE_ID, 'orange',
        `הערה: תוקף רישיון הרכב מסתיים בקרוב (${formatted}). נותרו כ-${info.daysLeft} ימים.`,
        { showRenewBtn: true });
    } else {
      setBanner(hostEl, BANNER_LICENSE_ID, 'green',
        `רישיון הרכב בתוקף עד ${formatted}.`);
    }
  } catch (err) {
    log.warn('renderLicense failed:', err);
    setBanner(hostEl, BANNER_LICENSE_ID, 'orange', 'לא ניתן לאמת את תוקף הרישיון כעת.');
  }
}

/** Official Israeli MOT online vehicle license renewal portal. */
const LICENSE_RENEWAL_URL = 'https://ecom.gov.il/voucherspa/input/260';

/**
 * Open the gov.il vehicle-license renewal portal in the system browser.
 * `window.open(_, '_blank')` is forwarded to the OS browser by Capacitor's
 * WebView, so no extra plugin is required.
 */
function openLicenseRenewal() {
  try { window.open(LICENSE_RENEWAL_URL, '_blank'); }
  catch (err) { log.warn('openLicenseRenewal failed:', err); }
}

/**
 * @param {HTMLElement} hostEl
 * @param {string} plate
 */
async function renderOwnership(hostEl, plate) {
  try {
    const [govLast, fbStoredFirst, mismatchFlag] = await Promise.all([
      getLastOwnership(plate),
      getStoredOwnershipISO(plate),
      readOnce(`vehicles/${plate}/ownershipMismatch`),
    ]);
    const gov = govLast?.yearMonth ?? null;
    let fbStored = fbStoredFirst;

    // Self-heal: if the gov-side ownership is known but nothing is cached on
    // the vehicle record, write it now. This avoids the "אי-ודאות" banner
    // showing right after registration (when the registration flow forgot to
    // persist lastOwnership) and on every subsequent load.
    if (govLast?.dateISO && !fbStored) {
      try {
        const phone = LocalState.phone ?? '';
        await persistOwnership(plate, phone, govLast);
        fbStored = govLast.dateISO;
      } catch (e) { log.warn('self-heal persistOwnership failed:', e); }
    }

    const fb = isoToYearMonth(fbStored);
    const forcedMismatch = String(mismatchFlag ?? '0') === '1';

    if (gov && fb) {
      if (gov === fb && !forcedMismatch) {
        const since = formatYearMonthHebrew(gov);
        setOwnershipBanner(
          hostEl,
          'match',
          '✓ הבעלות אומתה',
          since
            ? `הרכב בבעלותך מאז ${since} — תואם לרישומי משרד התחבורה.`
            : 'פרטי הבעלות תואמים לרישומי משרד התחבורה.',
        );
      } else {
        setOwnershipBanner(hostEl, 'mismatch', 'התראת בעלות', 'זוהה שינוי ברישום הבעלות לרכב — יש לעדכן את הרכב המשויך לחשבון.');
      }
    } else if (gov || fb) {
      setOwnershipBanner(hostEl, 'partial', 'אי-ודאות בבעלות', 'לא הצלחנו לאמת באופן מלא את הבעלות על רכבך.');
    } else {
      // No gov record AND no fb record — typical for vehicles whose last
      // ownership change predates January 2017 (the public `shinui_baalut`
      // dataset's earliest entry). Not a failure on our side — phrase it
      // accordingly so the user isn't alarmed.
      setOwnershipBanner(
        hostEl,
        'unknown',
        'מידע על מועד הבעלות חסר',
        'הרכב שלך רשום אצלנו, מועד רכישת הרכב מתועד רק עבור רכבים משנת ייצור 2017.',
      );
    }
  } catch (err) {
    log.warn('renderOwnership failed:', err);
  }
  void ref;
}

/**
 * Convert a `YYYY-MM` string into a friendly Hebrew month-year label,
 * e.g. `"2020-06"` → `"יוני 2020"`. Returns null on bad input so the
 * caller can fall back to a generic message.
 *
 * @param {string | null | undefined} yearMonth
 * @returns {string | null}
 */
function formatYearMonthHebrew(yearMonth) {
  const m = String(yearMonth || '').match(/^(\d{4})-(\d{1,2})$/);
  if (!m) return null;
  const year = m[1];
  const monthNum = parseInt(m[2], 10);
  if (monthNum < 1 || monthNum > 12) return null;
  const months = [
    'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
  ];
  return `${months[monthNum - 1]} ${year}`;
}

const LICENSE_COLORS = /** @type {Record<string, string>} */ ({
  red: 'background:#fdecea;color:#a94442;border:1px solid #f5c6cb',
  orange: 'background:#fff8e1;color:#8a6d3b;border:1px solid #ffe8a1',
  green: 'background:#e9f7ef;color:#1e7e34;border:1px solid #c3e6cb',
  unknown: 'background:#eef5ff;color:#0b5ed7;border:1px solid #cfe2ff',
});

const OWNERSHIP_COLORS = /** @type {Record<string, string>} */ ({
  match: 'background:#e9f7ef;color:#1e7e34;border:1px solid #c3e6cb',
  mismatch: 'background:#fdecea;color:#a94442;border:1px solid #f5c6cb',
  partial: 'background:#fff8e1;color:#8a6d3b;border:1px solid #ffe8a1',
  unknown: 'background:#eef5ff;color:#0b5ed7;border:1px solid #cfe2ff',
});

const BANNER_BASE = 'border-radius:10px;padding:12px 14px;margin:8px 0;font-weight:600;';

/** Gradient + shadow combinations for the renewal button, keyed by banner kind.
 *  Designed to read as a clear CTA without clashing with the soft banner bg. */
const RENEW_BTN_STYLE = /** @type {Record<string, string>} */ ({
  red:    'background:linear-gradient(135deg,#ef4444 0%,#b91c1c 100%);box-shadow:0 4px 12px rgba(239,68,68,.3);',
  orange: 'background:linear-gradient(135deg,#f59e0b 0%,#b45309 100%);box-shadow:0 4px 12px rgba(245,158,11,.3);',
});

/**
 * @typedef {Object} BannerOpts
 * @property {boolean} [showRenewBtn]  Append a full-width "חידוש רישיון אונליין" CTA below the message.
 */

/**
 * @param {HTMLElement} host
 * @param {string} id
 * @param {string} kind
 * @param {string} text
 * @param {BannerOpts} [opts]
 */
function setBanner(host, id, kind, text, opts = {}) {
  let box = host.querySelector(`#${id}`);
  if (!box) {
    box = document.createElement('div');
    box.id = id;
    box.className = id === BANNER_LICENSE_ID ? 'license-banner' : 'ownership-banner';
    host.prepend(box);
  }
  const color = LICENSE_COLORS[kind] ?? LICENSE_COLORS.unknown;
  /** @type {HTMLElement} */ (box).style.cssText = BANNER_BASE + color;

  // Build content programmatically so the message stays text-only (no XSS
  // risk if someone ever passes user-provided text through here) and the
  // button is a real element with a click listener.
  while (box.firstChild) box.removeChild(box.firstChild);
  const msgDiv = document.createElement('div');
  msgDiv.textContent = text;
  box.appendChild(msgDiv);

  if (opts.showRenewBtn && (kind === 'red' || kind === 'orange')) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'license-renew-btn';
    btn.style.cssText =
      'appearance:none;border:0;width:100%;padding:11px;margin-top:10px;' +
      'border-radius:10px;color:#fff;font:inherit;font-size:14px;font-weight:700;' +
      'cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:8px;' +
      'transition:opacity .15s,transform .1s;' +
      (RENEW_BTN_STYLE[kind] || '');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="#fff" aria-hidden="true">' +
        '<path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7zM19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/>' +
      '</svg>' +
      '<span>חידוש רישיון אונליין</span>';
    btn.addEventListener('click', (e) => { e.stopPropagation(); openLicenseRenewal(); });
    box.appendChild(btn);
  }

  // Keep legacy class names for any CSS that might still target them.
  box.classList.remove('license-red', 'license-orange', 'license-green');
  if (id === BANNER_LICENSE_ID && kind !== 'unknown') {
    box.classList.add(`license-${kind}`);
  }
}

/**
 * @param {HTMLElement} host
 * @param {string} status
 * @param {string} title
 * @param {string} desc
 */
function setOwnershipBanner(host, status, title, desc) {
  let box = host.querySelector(`#${BANNER_OWNERSHIP_ID}`);
  if (!box) {
    box = document.createElement('div');
    box.id = BANNER_OWNERSHIP_ID;
    box.className = 'ownership-banner';
    host.prepend(box);
  }
  const color = OWNERSHIP_COLORS[status] ?? OWNERSHIP_COLORS.unknown;
  /** @type {HTMLElement} */ (box).style.cssText = BANNER_BASE + color;
  box.innerHTML = `<div>${title}</div><div style="font-weight:500;margin-top:4px;font-size:14px;">${desc}</div>`;
  box.setAttribute('data-status', status);
}
